import React, { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crop, Trash2, Copy, Check, ImagePlus, Star } from 'lucide-react';
import { getImageUrl } from '../../config/api';
import { PhotoLightbox } from '../PhotoLightbox';
import './AdminGallery.css';

const AdminPhotoMasonry = ({
  photos = [],
  albumName,
  onAddPhotos,
  onCropHero,
  onDelete,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const navigateLightbox = useCallback(
    (delta) => {
      setLightboxIndex((prev) => {
        if (prev === null || photos.length === 0) return prev;
        return (prev + delta + photos.length) % photos.length;
      });
    },
    [photos.length]
  );

  const copyUrl = (photo) => {
    navigator.clipboard.writeText(getImageUrl(photo.image_url));
    setCopiedId(photo.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const currentPhoto = lightboxIndex !== null ? photos[lightboxIndex] : null;

  if (photos.length === 0) {
    return (
      <div className="admin-gallery-empty">
        <ImagePlus size={40} strokeWidth={1.2} />
        <h3>No photos in this album</h3>
        <p>Upload photos — they appear in the same masonry layout guests see on the site.</p>
        <button type="button" className="btn-primary" onClick={onAddPhotos}>
          <ImagePlus size={18} /> Add photos
        </button>
      </div>
    );
  }

  return (
    <div className="admin-gallery-photos">
      <div className="admin-gallery-masonry">
        {photos.map((photo, index) => (
          <motion.div
            key={photo.id}
            className="admin-gallery-item"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.02, 0.35) }}
          >
            <button
              type="button"
              className="admin-gallery-item-view"
              onClick={() => setLightboxIndex(index)}
            >
              <img
                src={getImageUrl(photo.image_url)}
                alt={photo.caption || 'Gallery photo'}
                loading="lazy"
              />
            </button>
            {Number(photo.show_in_hero) === 1 && (
              <span className="admin-gallery-hero-badge" title="Shown in homepage hero">
                <Star size={12} fill="currentColor" /> Hero
              </span>
            )}
            <div className="admin-gallery-item-actions">
              <button type="button" title="Crop for hero" onClick={() => onCropHero(photo)}>
                <Crop size={16} />
              </button>
              <button type="button" title="Copy image URL" onClick={() => copyUrl(photo)}>
                {copiedId === photo.id ? <Check size={16} /> : <Copy size={16} />}
              </button>
              <button type="button" title="Delete" className="danger" onClick={() => onDelete(photo)}>
                <Trash2 size={16} />
              </button>
            </div>
            {(photo.caption || photo.uploaded_at) && (
              <div className="admin-gallery-item-meta">
                {photo.caption && <p>{photo.caption}</p>}
              </div>
            )}
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <PhotoLightbox
            photos={photos.map((p) => ({ ...p, albumName }))}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onNavigate={navigateLightbox}
            footer={
              currentPhoto && (
                <div className="photo-lightbox-admin-bar">
                  <button type="button" onClick={() => onCropHero(currentPhoto)}>
                    <Crop size={18} /> Hero crop
                  </button>
                  <button type="button" onClick={() => copyUrl(currentPhoto)}>
                    {copiedId === currentPhoto.id ? <Check size={18} /> : <Copy size={18} />} Copy URL
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => {
                      onDelete(currentPhoto);
                      setLightboxIndex(null);
                    }}
                  >
                    <Trash2 size={18} /> Delete
                  </button>
                </div>
              )
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPhotoMasonry;
